import { Inject, Injectable } from '@angular/core';
import { from, OperatorFunction } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { INewRelicService } from '../../../new-relic/new-relic-service.interface';
import { NEW_RELIC_SERVICE_TOKEN } from '../../../new-relic/new-relic-token';

@Injectable()
export class PhotoCompressionService {
    constructor(
        @Inject(NEW_RELIC_SERVICE_TOKEN)
        private _newRelicService: INewRelicService
    ) {}
    compressTo(maxBytes: number): OperatorFunction<File[], File[]> {
        return (source) =>
            source.pipe(
                mergeMap((files) =>
                    from(
                        Promise.all(
                            files.map((f) => this.tryCompress(f, maxBytes))
                        )
                    )
                )
            );
    }

    private async tryCompress(file: File, maxBytes: number): Promise<File> {
        if (this.shouldSkip(file, maxBytes)) return file;
        try {
            return await this.compress(file, maxBytes);
        } catch (e) {
            this._newRelicService.noticeError(e instanceof Error ? e : new Error(String(e)), false);
            return file;
        }
    }

    private shouldSkip(file: File, maxBytes: number): boolean {
        return file.type === 'image/gif' || file.size <= maxBytes;
    }

    private async compress(file: File, maxBytes: number): Promise<File> {
        const bitmap = await createImageBitmap(file);
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        const ctx = canvas.getContext('2d');
        try {
            if (!ctx) throw new Error('canvas 2d context unavailable');
            ctx.drawImage(bitmap, 0, 0);
        } finally {
            bitmap.close();
        }

        let blob!: Blob;
        for (let q = 9; q >= 1; q--) {
            blob = await this.canvasToBlob(canvas, q / 10);
            if (blob.size <= maxBytes) break;
        }

        if (blob.size > maxBytes) return file;

        const newName = file.name.replace(/\.[^.]+$/, '.jpg');
        return new File([blob], newName, { type: 'image/jpeg' });
    }

    private canvasToBlob(
        canvas: HTMLCanvasElement,
        quality: number
    ): Promise<Blob> {
        return new Promise((resolve, reject) => {
            canvas.toBlob(
                (blob) =>
                    blob ? resolve(blob) : reject(new Error('toBlob failed')),
                'image/jpeg',
                quality
            );
        });
    }
}
